/* eslint-disable security/detect-object-injection */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from '@/lib/storage';
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { io, Socket } from 'socket.io-client';

// Types
interface CallState {
  callRequests: Record<string, any[]>;
  isInitialized: boolean; // Track if initialization is done
  acceptedRequestId: string | null | undefined;
  connectionStatus: string | null;
  errorMessage: string | null;
  expertToken: string | null | undefined;
  meetingId: string | null | undefined;
  interviewId: string | null | undefined;
  sessionId: string | null | undefined;
  screen: string | null | undefined;
  user: any;
  interviewerId: string | null | undefined;
  joinedRoom: string | null;
  timeout: boolean;
  acceptedCallType: string | null | undefined;
  isRoomJoined: boolean;
  socket: any; // For managing socket connection
  time: any; // For managing socket connection
  totalTime: any; // For managing socket connection
  progress: any; // For managing socket connection
  roomId: string[] | null; // Stores room ids
  callConnectedTime: any;
  isOverwhelmed: boolean;
}

// Initial State
const initialState: CallState = {
  callRequests: {},
  isInitialized: false, // Initially not initialized
  acceptedRequestId: null,
  connectionStatus: null,
  errorMessage: null,
  expertToken: null,
  meetingId: null,
  interviewId: null,
  sessionId: null,
  user: null,
  interviewerId: null,
  joinedRoom: null,
  timeout: false,
  isRoomJoined: false,
  socket: null,
  roomId: null,
  screen: 'unlock',
  acceptedCallType: null,
  time: 180, // 3 minutes in seconds
  totalTime: 180,
  progress: 0,
  callConnectedTime: 0,
  isOverwhelmed: false,
};

// Async Thunks to handle socket connection
export const initializeSocket = createAsyncThunk(
  'call/initializeSocket',
  async ({ token, roomId }: { token: string | null; roomId: string[] }, { dispatch }) => {
    try {
      // const acceptedRequestId = useSelector((state: any) => state.call);

      const socketInstance: Socket = io(process.env.NEXT_PUBLIC_SOCKET_URL, {
        auth: { token: `Bearer ${token}` },
      });

      // Dispatch socket instance to Redux
      dispatch(setSocket(socketInstance));

      // Handle socket connection
      socketInstance.on('connect', () => {
        if (roomId && roomId.length > 0) {
          roomId.forEach((room: any) => {
            socketInstance.emit('joinRoom', room?.room);
          });
        }
      });

      // Event for setIsOverwhelmed
      socketInstance.on('upgradeReminder', () => {
        dispatch(setIsOverwhelmed(true));
      });

      // Listen to events
      socketInstance.on('callRequest', (data) => {
        dispatch(addCallRequest({ id: data.roomId, callRequest: data }));
      });

      socketInstance.on('removeCallRequest', (data) => {
        dispatch(removeCallRequest({ requestId: data.requestId, roomId: data.roomId }));
        dispatch(setTimedOut(true));
      });

      socketInstance.on('connectSuccess', (data: any) => {
        dispatch(setCallScreen('review'));
        dispatch(setConnectionStatus('success'));
        dispatch(setAcceptedRequestId(data?.interviewId || data?.requestId));
        dispatch(setMeetingId(data?.videoRoomId));
        dispatch(setExpertToken(data?.token));
        dispatch(setInterviewId(data?.interviewId));
        dispatch(setInterviewerId(data?.interviewerId || data?.expertId));
        dispatch(setUser(data?.newExpert || data?.userId));
        dispatch(setSessionId(data?.sessionId));
        dispatch(setErrorMessage(null));
        dispatch(removeCallRequest({ requestId: data?.interviewId || data?.requestId, roomId: data.roomId }));
      });

      // Interview request
      socketInstance?.on('interviewRequest', (data: any) => {
        dispatch(addCallRequest({ id: data.roomId, callRequest: data }));
      });

      socketInstance?.on('removeInterviewRequest', (data: any) => {
        dispatch(removeCallRequest({ requestId: data.requestId, roomId: data.roomId }));
        dispatch(setTimedOut(true));
      });

      socketInstance?.on('requestTaken', (data: any) => {
        // Assuming 'callRequests' is being managed by Redux and fetched elsewhere in your component using useSelector
        dispatch(removeCallRequest({ requestId: data.requestId, roomId: data.roomId }));
        dispatch(setTimedOut(true));
      });

      // Listen when caller choose the call type
      socketInstance?.on('meetCustomer', (data: any) => {
        dispatch(connectBothUser(data));
        // }
      });
      socketInstance?.on('callerQuitNotification', (data: any) => {
        dispatch(removeCallRequest({ requestId: data.requestId, roomId: data?.roomId }));

        dispatch(setScreenBecauseCallerNotPicked(data));
        removeSessionStorageItem('meetingData');
      });
      socketInstance?.on('callerQuitFailed', () => {});
      socketInstance?.on('callerLeft', (data: any) => {
        dispatch(removeCallRequest({ requestId: data.requestId, roomId: data?.roomId }));
        dispatch(setScreenBecauseCallerNotPicked(data));
        removeSessionStorageItem('meetingData');
      });

      return socketInstance; // return socket instance after setup
    } catch (error) {
      console.error('Error initializing socket:', error);
      throw new Error('Error initializing socket');
    }
  },
);

// Slice
const callSlice = createSlice({
  name: 'call',
  initialState,
  reducers: {
    setSocket: (state, action: PayloadAction<Socket>) => {
      state.socket = action.payload;
    },

    acceptCall: (state, action: PayloadAction<any>) => {
      if (state.socket) {
        const { requestId } = action.payload;
        state.socket.emit('connectRequest', { requestId }); // Emit socket event to accept the call
      }
    },

    acceptInterview: (state, action: PayloadAction<any>) => {
      if (state.socket) {
        const { requestId } = action.payload;
        state.socket.emit('acceptInterview', { requestId }); // Emit socket event to accept the interview
      }
    },

    // Call Request reducers
    initializeCallRequest: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      if (!state.callRequests[id]) {
        state.callRequests[id] = []; // Initialize an empty array for the room if not already present
      }
    },
    // addCallRequest: (state, action: PayloadAction<{ id: string; callRequest: any }>) => {
    //   const { id, callRequest } = action.payload;
    //   if (!state.callRequests[id]) {
    //     state.callRequests[id] = []; // Initialize an empty array for the room if not already present
    //   }
    //   state.callRequests[id].push(callRequest); // Add the new call request to the room
    // },
    addCallRequest: (state, action: PayloadAction<{ id: string; callRequest: any }>) => {
      const { id, callRequest } = action.payload;
      if (!state.callRequests[id]) {
        state.callRequests[id] = []; // Initialize an empty array for the room if not already present
      }

      // Check if the requestId already exists in the callRequests array
      const existingRequestIndex = state.callRequests[id].findIndex((req) => req.requestId === callRequest.requestId);

      if (existingRequestIndex === -1) {
        // If the requestId does not exist, push the new callRequest
        state.callRequests[id].push(callRequest);
      } else {
        // Optionally, replace the existing request or do nothing
        // Replace with the new callRequest if you want to update it
        state.callRequests[id][existingRequestIndex] = callRequest;
      }
    },
    removeCallRequest: (state, action: PayloadAction<{ requestId: string; roomId: string }>) => {
      const { requestId, roomId } = action.payload;

      // Remove the request with the matching requestId from the specified room
      if (state.callRequests[roomId]) {
        state.callRequests[roomId] = state.callRequests[roomId].filter(
          (req) => req.requestId !== requestId, // Remove the request with the matching requestId
        );

        // Clean up empty arrays (remove the room if no requests remain)
        if (state.callRequests[roomId].length === 0) {
          delete state.callRequests[roomId]; // Delete the room entry if there are no requests left
        }
      }
    },

    // Socket and Room management reducers
    setIsOverwhelmed: (state, action: PayloadAction<boolean>) => {
      state.isOverwhelmed = action.payload; // Sets the list of room IDs
    },
    setRoomId: (state, action: PayloadAction<string[] | null>) => {
      state.roomId = action.payload; // Sets the list of room IDs
    },
    setJoinRoom: (state, action: PayloadAction<string | null>) => {
      state.joinedRoom = action.payload; // Sets the current room ID joined
    },
    setIsRoomJoined: (state, action: PayloadAction<boolean>) => {
      state.isRoomJoined = action.payload; // Tracks if the room has been joined
    },
    setConnectionStatus: (state, action: PayloadAction<string | null>) => {
      state.connectionStatus = action.payload; // Sets connection status (e.g., connected, disconnected)
    },
    setErrorMessage: (state, action: PayloadAction<string | null>) => {
      state.errorMessage = action.payload; // Sets any error message
    },

    // Other Call State reducers
    setAcceptedRequestId: (state, action: PayloadAction<string | null | undefined>) => {
      state.acceptedRequestId = action.payload; // Sets the ID of the accepted call request
    },
    setMeetingId: (state, action: PayloadAction<string | null | undefined>) => {
      state.meetingId = action.payload; // Sets the meeting ID
    },
    setExpertToken: (state, action: PayloadAction<string | null | undefined>) => {
      state.expertToken = action.payload; // Sets the expert token
    },
    setInterviewId: (state, action: PayloadAction<string | null | undefined>) => {
      state.interviewId = action.payload; // Sets the interview ID
    },
    setInterviewerId: (state, action: PayloadAction<string | null | undefined>) => {
      state.interviewerId = action.payload; // Sets the interviewer ID
    },
    setUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload; // Sets the user
    },
    setSessionId: (state, action: PayloadAction<string | null | undefined>) => {
      state.sessionId = action.payload; // Sets the session ID
    },
    setTimedOut: (state, action: PayloadAction<boolean>) => {
      if (state.screen === 'review') {
        state.timeout = action.payload; // Tracks if the call timed out
      }
    },
    setCallScreen: (state, action: PayloadAction<string | null | undefined>) => {
      state.screen = action.payload; // Tracks if the call timed out
    },
    setAcceptedCallType: (state, action: PayloadAction<string | null | undefined>) => {
      state.acceptedCallType = action.payload; // Tracks if the call timed out
    },
    setCallConnectedTime: (state, action: PayloadAction<number | null | undefined>) => {
      state.callConnectedTime = action.payload; // Tracks if the call timed out
    },
    setScreenBecauseCallerNotPicked: (state, action: PayloadAction<any>) => {
      if (state.acceptedRequestId === action.payload.requestId) {
        try {
          state.screen = 'sorry';
          return;
        } catch (error) {
          console.error(error);
        }
      }
    },
    setTime: (state, action: PayloadAction<number>) => {
      state.time = action.payload;
      state.progress = ((state.totalTime - state.time) / state.totalTime) * 100;
    },
    resetTime: (state) => {
      state.time = state.totalTime;
      state.progress = 0;
    },
    connectBothUser: (state, action: PayloadAction<any>) => {
      if (state.acceptedRequestId === action.payload.requestId) {
        setSessionStorageItem('callType', action?.payload?.callType);
        if (action?.payload?.callType === 'audio' || action?.payload?.callType === 'video') {
          state.acceptedCallType = action.payload?.callType;
          const meetingData = getSessionStorageItem('meetingData');
          setSessionStorageItem('meetingData', {
            ...(typeof meetingData === 'object' && meetingData !== null ? meetingData : {}),
            choosedCallType: action.payload?.callType,
          });
          state.screen = 'ongoing';
          return;
        } else {
          removeSessionStorageItem('meetingData');
          state.screen = 'sorry';
        }
      }
    },

    // Action to clear the call state
    clearCallState: (state) => {
      removeSessionStorageItem('deductionData');
      removeSessionStorageItem('meetingData');
      removeSessionStorageItem('callType');
      removeSessionStorageItem('callConnected');
      removeSessionStorageItem('callTypeConverted');
      removeSessionStorageItem('time');
      state.acceptedRequestId = null;
      state.connectionStatus = null;
      state.errorMessage = null;
      state.expertToken = null;
      state.meetingId = null;
      state.interviewId = null;
      state.sessionId = null;
      state.user = null;
      state.interviewerId = null;
      state.roomId = null;
      state.isRoomJoined = false;
      state.timeout = false;
      state.screen = 'unlock';
      state.acceptedCallType = null;
      state.callConnectedTime = 0;
    },
  },
});

export const {
  setSocket,
  setRoomId,
  setJoinRoom,
  setIsRoomJoined,
  acceptCall,
  acceptInterview,
  initializeCallRequest,
  addCallRequest,
  removeCallRequest,
  setConnectionStatus,
  setErrorMessage,
  clearCallState,
  setAcceptedRequestId,
  setMeetingId,
  setExpertToken,
  setInterviewId,
  setInterviewerId,
  setUser,
  setSessionId,
  setTimedOut,
  setCallScreen,
  setAcceptedCallType,
  connectBothUser,
  setTime,
  resetTime,
  setCallConnectedTime,
  setScreenBecauseCallerNotPicked,
  setIsOverwhelmed,
} = callSlice.actions;

export default callSlice.reducer;
